import React from "react";
import { cn } from "../../utils/cn";

const Input = React.forwardRef(({
    className,
    type = "text",
    label,
    description,
    error,
    required = false,
    id,
    autoComplete,
    ...props
}, ref) => {
    // Generate unique ID if not provided
    const inputId = id || `input-${Math.random()?.toString(36)?.substr(2, 9)}`;

    // Base input classes
    const baseInputClasses = "flex h-10 w-full rounded-md border border-white/20 bg-black px-4 py-2 text-base font-medium text-white ring-offset-background file:border-0 file:bg-transparent file:text-base file:font-medium placeholder:text-white/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:border-white/40 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200";

    // Checkbox-specific styles
    if (type === "checkbox") {
        return (
            <input
                type="checkbox"
                className={cn(
                    "h-5 w-5 rounded border border-white/20 bg-black text-white focus:ring-2 focus:ring-white/50 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200",
                    className
                )}
                ref={ref}
                id={inputId}
                aria-invalid={error ? "true" : "false"}
                aria-describedby={error ? `${inputId}-error` : description ? `${inputId}-description` : undefined}
                {...props}
            />
        );
    }

    // Radio button-specific styles
    if (type === "radio") {
        return (
            <input
                type="radio"
                className={cn(
                    "h-5 w-5 rounded-full border border-white/20 bg-black text-white focus:ring-2 focus:ring-white/50 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200",
                    className
                )}
                ref={ref}
                id={inputId}
                aria-invalid={error ? "true" : "false"}
                aria-describedby={error ? `${inputId}-error` : description ? `${inputId}-description` : undefined}
                {...props}
            />
        );
    }

    // For regular inputs with wrapper structure
    return (
        <div className="space-y-2">
            {label && (
                <label
                    htmlFor={inputId}
                    className={cn(
                        "text-base font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-white",
                        error ? "text-white/70" : "text-white"
                    )}
                >
                    {label}
                    {required && <span className="text-white/70 ml-1" aria-label="required">*</span>}
                </label>
            )}

            <input
                type={type}
                className={cn(
                    baseInputClasses,
                    error && "border-white/40 focus-visible:ring-white/50 focus-visible:border-white/50",
                    className
                )}
                ref={ref}
                id={inputId}
                autoComplete={autoComplete}
                aria-invalid={error ? "true" : "false"}
                aria-describedby={error ? `${inputId}-error` : description ? `${inputId}-description` : undefined}
                aria-required={required}
                {...props}
            />

            {description && !error && (
                <p id={`${inputId}-description`} className="text-base text-white/70">
                    {description}
                </p>
            )}

            {error && (
                <p id={`${inputId}-error`} className="text-base text-white/70" role="alert">
                    {error}
                </p>
            )}
        </div>
    );
});

Input.displayName = "Input";

export default Input;